import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { auth, googleProvider, appleProvider } from "../firebase";
import { signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";

const LoginPage = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError("");

      await signInWithEmailAndPassword(auth, email.trim(), password);
      redirectAfterLogin(navigate);
    } catch (err) {
      console.error("Login failed:", err);
      setError(getFriendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const loginWithProvider = async (provider) => {
    try {
      setLoading(true);
      setError("");

      await signInWithPopup(auth, provider);
      redirectAfterLogin(navigate);
    } catch (err) {
      console.error("Provider login failed:", err);
      setError(getFriendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <p style={eyebrowStyle}>Antarctic Press Portal</p>

        <h1 style={titleStyle}>Welcome back</h1>

        <p style={subtitleStyle}>
          Log in to continue reading, track backed campaigns, and personalize
          your Antarctic Press experience.
        </p>

        {error ? <div style={errorStyle}>{error}</div> : null}

        <form onSubmit={handleLogin} style={formStyle}>
          <label style={labelStyle}>
            Email
            <input
              type="email"
              value={email}
              autoComplete="email"
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
              disabled={loading}
              required
            />
          </label>

          <label style={labelStyle}>
            Password
            <input
              type="password"
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
              disabled={loading}
              required
            />
          </label>

          <label style={checkboxRowStyle}>
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={() => setRememberMe((value) => !value)}
              disabled={loading}
            />
            <span>Remember me</span>
          </label>

          <Link to="/portal/forgot-password" style={forgotLinkStyle}>
            Forgot your password?
          </Link>

          <button type="submit" style={primaryButtonStyle} disabled={loading}>
            {loading ? "Logging in..." : "Log In"}
          </button>
        </form>

        <div style={dividerStyle}>
          <span style={dividerLineStyle} />
          <span style={dividerTextStyle}>or</span>
          <span style={dividerLineStyle} />
        </div>

        <div style={socialStackStyle}>
          <button
            type="button"
            style={socialButtonStyle}
            onClick={() => loginWithProvider(googleProvider)}
            disabled={loading}
          >
            Continue with Google
          </button>

{/*           <button
            type="button"
            style={socialButtonStyle}
            onClick={() => loginWithProvider(appleProvider)}
            disabled={loading}
          >
            Continue with Apple
          </button> */}
        </div>

        <p style={footerTextStyle}>
          New to our platform?{" "}
          <Link to="/portal/signup" style={linkStyle}>
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
};

function getSafeReturnPath(search) {
  const params = new URLSearchParams(search);
  const raw = params.get("return");
  if (!raw) return null;
  if (!raw.startsWith("/") || raw.startsWith("//") || raw.includes(":")) return null;
  return raw;
}

function redirectAfterLogin(navigate) {
  const dest = getSafeReturnPath(window.location.search);
  if (dest) {
    window.location.assign(dest);
  } else {
    navigate("/");
  }
}

function getFriendlyAuthError(err) {
  switch (err?.code) {
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "The email or password you entered is incorrect.";
    case "auth/popup-closed-by-user":
      return "The sign-in window was closed before completing.";
    default:
      return "Something went wrong while logging in.";
  }
}

const pageStyle = {
  minHeight: "calc(100vh - 80px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "3rem 1rem",
  background: "#f3f6fa",
};

const cardStyle = {
  width: "100%",
  maxWidth: "460px",
  background: "#fff",
  border: "1px solid #d7e2ef",
  borderTop: "5px solid #114c96",
  borderRadius: "14px",
  padding: "2rem",
  boxShadow: "0 10px 28px rgba(0,0,0,0.08)",
};

const eyebrowStyle = {
  margin: "0 0 0.5rem 0",
  color: "#114c96",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  fontSize: "0.78rem",
  fontWeight: 800,
};

const titleStyle = {
  margin: "0 0 0.75rem 0",
  color: "#111827",
  fontSize: "2rem",
  fontWeight: 900,
};

const subtitleStyle = {
  margin: "0 0 1.5rem 0",
  color: "#4b5563",
  lineHeight: 1.6,
};

const formStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
};

const labelStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "0.4rem",
  color: "#111827",
  fontWeight: 700,
};

const inputStyle = {
  padding: "0.8rem 0.9rem",
  border: "1px solid #cbd5e1",
  borderRadius: "8px",
  fontSize: "1rem",
};

const checkboxRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  color: "#4b5563",
  fontWeight: 600,
};

const primaryButtonStyle = {
  marginTop: "0.5rem",
  background: "#114c96",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  padding: "0.85rem 1rem",
  fontWeight: 800,
  cursor: "pointer",
};

const socialStackStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "0.75rem",
};

const socialButtonStyle = {
  width: "100%",
  background: "#fff",
  color: "#111827",
  border: "1px solid #cbd5e1",
  borderRadius: "8px",
  padding: "0.85rem 1rem",
  fontWeight: 800,
  cursor: "pointer",
};

const dividerStyle = {
  display: "flex",
  alignItems: "center",
  gap: "0.75rem",
  margin: "1.5rem 0",
};

const dividerLineStyle = {
  height: "1px",
  background: "#d7e2ef",
  flex: 1,
};

const dividerTextStyle = {
  color: "#64748b",
  fontWeight: 700,
};

const errorStyle = {
  background: "#fff1f2",
  color: "#be123c",
  border: "1px solid #fecdd3",
  borderRadius: "8px",
  padding: "0.85rem",
  marginBottom: "1rem",
  fontWeight: 700,
};

const footerTextStyle = {
  margin: "1.5rem 0 0 0",
  color: "#4b5563",
  textAlign: "center",
};

const linkStyle = {
  color: "#114c96",
  fontWeight: 800,
};

const forgotLinkStyle = {
  alignSelf: "flex-start",
  color: "#114c96",
  fontWeight: 700,
  fontSize: "0.9rem",
};

export default LoginPage;